/**
 * Thin typed fetch wrapper used by every service module.
 *
 * Auth: an Auth0 access token is injected as `Authorization: Bearer ...` via
 * the async `getToken()` callback the host app provides at construction time
 * (so token refresh stays out of api-client). Every request emits an
 * `X-Request-Id` header for tracing; the value can be set per-call or
 * generated via `crypto.randomUUID()`.
 *
 * Errors: non-2xx responses throw {@link ApiClientError} carrying the
 * structured `ApiError` body when the backend returned one. Network or
 * parse failures throw with `code = "NETWORK"` / `"PARSE"`.
 */

import type { ApiError } from "@re-platform/types";

export interface ApiClientConfig {
  baseUrl: string;
  /** Returns a fresh Auth0 access token; called once per request. */
  getToken: () => Promise<string | null>;
  /** Optional default headers merged into every request. */
  defaultHeaders?: Record<string, string>;
  /**
   * Optional acting-tenant id for dual-licensed staff; when it returns a value
   * the request carries `X-Acting-Tenant-Id`, and the backend re-anchors the
   * session to that tenant after validating an active membership.
   */
  getActingTenantId?: () => string | null;
  /**
   * Called when a request comes back 401. Return true to retry it EXACTLY once
   * (with a freshly-fetched token), false to let the 401 surface.
   *
   * Refresh itself stays out of api-client — the host app owns the token, so it
   * owns the renewal. This is only the trigger. Implementations must be safe to
   * call concurrently: several in-flight requests can 401 together, and with
   * single-use refresh rotation a naive implementation revokes its own token.
   */
  onUnauthorized?: () => Promise<boolean>;
  /** Per-call timeout in ms. */
  timeoutMs?: number;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  requestId?: string;
  /**
   * Per-request acting tenant, overriding the client-wide `getActingTenantId`.
   *
   * Exists so a dual-licensed member of staff can see BOTH brokerages on one
   * screen: the merged dashboard fires the same query once per membership,
   * concurrently, each pinned to a different tenant. A client-wide setting
   * cannot express that — it is one value for the whole app, so showing two
   * tenants at once meant reloading between them.
   *
   * `undefined` = fall back to the client-wide value (the common case).
   * `null` = explicitly send NO header, i.e. act as the login tenant.
   */
  actingTenantId?: string | null;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly requestId: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export class ApiClient {
  constructor(private readonly config: ApiClientConfig) {}

  /**
   * A client identical to this one but pinned to a specific acting tenant.
   *
   * How a dual-licensed member of staff sees both brokerages on one screen: the
   * merged views fetch the same list once per membership, concurrently, each
   * through a client bound to a different tenant.
   *
   * Deliberately a derived CLIENT rather than an extra argument on every
   * service method — the alternative was threading an options parameter through
   * every list function on six services, and every future one, purely so a rare
   * caller could set one header. Existing service signatures are untouched.
   *
   * Shares the parent's `getToken`, so token refresh still happens in one place.
   */
  withActingTenant(tenantId: string): ApiClient {
    return new ApiClient({ ...this.config, getActingTenantId: () => tenantId });
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    try {
      return await this.attempt<T>(path, options);
    } catch (err) {
      if (
        err instanceof ApiClientError &&
        err.status === 401 &&
        this.config.onUnauthorized &&
        (await this.config.onUnauthorized())
      ) {
        // Once only. A second 401 after a successful renewal is a real
        // authorization failure, not a stale token, and retrying it forever
        // would hammer the API on every permission error.
        return await this.attempt<T>(path, options);
      }
      throw err;
    }
  }

  private async attempt<T>(
    path: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const url = new URL(path, this.config.baseUrl);
    if (options.query) {
      for (const [key, value] of Object.entries(options.query)) {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      }
    }

    const token = await this.config.getToken();
    const requestId =
      options.requestId ??
      globalThis.crypto?.randomUUID?.() ??
      `req-${Date.now()}`;

    const headers: Record<string, string> = {
      "X-Request-Id": requestId,
      ...this.config.defaultHeaders,
      ...options.headers,
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    // A per-request override wins over the client-wide value. `null` is
    // meaningful and distinct from `undefined`: it means "send no header" (act
    // as the login tenant), which `??` alone would collapse into the fallback.
    const actingTenant =
      options.actingTenantId !== undefined
        ? options.actingTenantId
        : this.config.getActingTenantId?.();
    if (actingTenant) headers["X-Acting-Tenant-Id"] = actingTenant;
    if (options.body !== undefined)
      headers["Content-Type"] = "application/json";

    const controller = options.signal ? null : new AbortController();
    const timer =
      this.config.timeoutMs && controller
        ? setTimeout(() => controller.abort(), this.config.timeoutMs)
        : null;

    let response: Response;
    try {
      response = await fetch(url, {
        method: options.method ?? "GET",
        headers,
        body:
          options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal ?? controller?.signal,
        // Auth is Bearer-token only (see module doc) — nothing in this codebase
        // ever sets or reads a cookie. `credentials:"include"` forced the API's
        // CORS to carry Access-Control-Allow-Credentials:true, which in turn
        // forbade a wildcard Access-Control-Allow-Origin — so every per-tenant
        // custom domain (V026) had to be individually allowlisted, and never
        // was. Dropping it lets the API open CORS to any origin safely.
      });
    } catch (err) {
      throw new ApiClientError(
        err instanceof Error ? err.message : "Network failure",
        0,
        "NETWORK",
        requestId,
      );
    } finally {
      if (timer) clearTimeout(timer);
    }

    if (response.status === 204) return undefined as T;

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new ApiClientError(
        "Invalid JSON response",
        response.status,
        "PARSE",
        requestId,
      );
    }

    if (!response.ok) {
      const apiErr = payload as Partial<ApiError> & { detail?: unknown };
      // FastAPI raises `{detail: ...}` — a string for a plain HTTPException, or an
      // object (e.g. our `{code, message, seats}` seat-limit error). Surface it so
      // callers get the real message + machine code instead of a generic status.
      const detail = apiErr.detail;
      const detailStr = typeof detail === "string" ? detail : undefined;
      const detailObj =
        detail && typeof detail === "object"
          ? (detail as Record<string, unknown>)
          : undefined;
      const detailMessage =
        detailStr ??
        (typeof detailObj?.message === "string"
          ? detailObj.message
          : undefined);
      const detailCode =
        typeof detailObj?.code === "string" ? detailObj.code : undefined;
      throw new ApiClientError(
        apiErr.message ??
          detailMessage ??
          `Request failed with status ${response.status}`,
        response.status,
        apiErr.code ?? detailCode ?? `HTTP_${response.status}`,
        apiErr.requestId ?? requestId,
        apiErr.details ?? detailObj,
      );
    }

    return payload as T;
  }
}
